import groq from "groq"

import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { getEmployeeReference } from "@/graphql/schemas/shared/utils/getEmployeeReference"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { agreedTourProjection } from "../sanity/tourPlanning.projection"

export const mutationUpdateAgreedTour = {
  async updateAgreedTour(parent, args, context, info) {
    const { isTourAgreed, calendarWeek, customerId, lang = AppSettings.defaultLocale() } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.TOURS,
      operation: Operations.UPDATE,
    })

    const customer = await getCustomer({ id: customerId })

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: false })

    const employeeNumber = user.employeeNumber
    const admNumber = user.loggedInAdm.admNumber

    if (isTourAgreed) {
      return createAgreedTour(client, calendarWeek, admNumber, employeeNumber, customer.customerNumber)
    } else {
      return deleteAgreedTour(client, calendarWeek, customer.customerNumber, admNumber)
    }
  },
}

async function createAgreedTour(client, calendarWeek, admNumber, employeeNumber, customerNumber) {
  const creationBy = await getEmployeeReference({
    employeeNumber: employeeNumber,
    sanityClient: client,
  })

  const agreedTour = await getAgreedTour(client, calendarWeek, customerNumber, admNumber)

  // if tour is already agreed, return without changes
  if (agreedTour) {
    return agreedTour
  }

  const doc = {
    _type: "agreedTour",
    customerNumber: customerNumber,
    calendarWeek: parseInt(calendarWeek),
    admNumber: admNumber,
    creationAt: new Date().toISOString(),
    ...(creationBy && { creationBy }),
  }

  // remove all undefined values
  Object.keys(doc).forEach((key) => doc[key] === undefined && delete doc[key])

  try {
    const updatedAgreedTour = await client.create(doc)

    return updatedAgreedTour ?? null
  } catch (error) {
    console.warn(error)
    return null
  }
}

async function deleteAgreedTour(client, calendarWeek, customerNumber, admNumber) {
  try {
    // make sure agreed tour exists and the id is actually of type agreedTour
    const agreedTour = await getAgreedTour(client, calendarWeek, customerNumber, admNumber)

    if (!agreedTour) return null

    const deleteAgreedTour = await client.delete(agreedTour.id)

    if (!deleteAgreedTour) return null

    return true
  } catch (error) {
    console.warn(error)
    return null
  }
}
async function getAgreedTour(client, calendarWeek, customerNumber, admNumber) {
  return await client.fetch(
    groq`*[_type=="agreedTour" && calendarWeek==$calendarWeek &&  customerNumber==$customerNumber && admNumber==$admNumber][0]{
            ${agreedTourProjection()}}`,

    { calendarWeek, customerNumber, admNumber },
  )
}
